import React, { useEffect, useMemo, useRef, useState } from 'react';
import { COUNTRIES, CountryPhoneInfo, flagEmoji } from './PhoneData';
import '../styles/Phoneinput.additions.scss'

/**
 * Reusable phone number field: a searchable country-code dropdown (all ~250
 * ISO countries/territories) plus the national number input.
 *
 * Controlled by the parent via `country` (the ISO2 code, e.g. "IN") so the
 * parent can reset it on its own (e.g. when a form's "role" changes) — an
 * internal-only useState here would survive re-renders and never reset.
 *
 * Emits digit-length validation info via `onValidate` isn't needed by the
 * parent directly; instead call `validatePhone(country, value)` (exported
 * below) wherever the parent runs its own validation.
 */
export interface PhoneInputProps {
  /** ISO2 of the currently selected country, e.g. "IN" */
  country: string;
  onCountryChange: (iso2: string) => void;
  /** National number, digits only (no dial code, no formatting) */
  value: string;
  onChange: (value: string) => void;
  error?: string;
  placeholder?: string;
}

function findCountry(iso2: string): CountryPhoneInfo {
  return (
    COUNTRIES.find((c) => c.iso2 === iso2) ??
    // sensible default if an unknown/blank code is passed in
    COUNTRIES.find((c) => c.iso2 === 'IN')!
  );
}

export default function PhoneInput({
  country,
  onCountryChange,
  value,
  onChange,
  error,
  placeholder = 'Contact Number *',
}: PhoneInputProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const selected = findCountry(country);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.dialCode.includes(q.replace(/^\+/, ''))
    );
  }, [query]);

  // close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  const handleSelect = (iso2: string) => {
    onCountryChange(iso2);
    onChange(''); // changing the country clears whatever number was typed
    setOpen(false);
    setQuery('');
  };

  return (
    <div>
      <div className="phone-row" ref={wrapRef} style={{ position: 'relative' }}>
        <button
          type="button"
          className="phone-code"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span>{flagEmoji(selected.iso2)}</span>
          <span>+{selected.dialCode}</span>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ marginLeft: 4 }}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        <input
          type="tel"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`phone-number${error ? ' phone-number--error' : ''}`}
        />

        {open && (
          <div className="phone-code-dropdown">
            <input
              ref={searchRef}
              type="text"
              placeholder="Search country or code..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="phone-code-dropdown__search"
            />
            <ul className="phone-code-dropdown__list" role="listbox">
              {filtered.length === 0 && (
                <li className="phone-code-dropdown__empty">No matches</li>
              )}
              {filtered.map((c) => (
                <li
                  key={c.iso2}
                  role="option"
                  aria-selected={c.iso2 === selected.iso2}
                  className={`phone-code-dropdown__item${c.iso2 === selected.iso2 ? ' phone-code-dropdown__item--active' : ''}`}
                  onClick={() => handleSelect(c.iso2)}
                >
                  <span>{flagEmoji(c.iso2)}</span>
                  {/* <span className="phone-code-dropdown__name">{c.name}</span> */}
                  <span className="phone-code-dropdown__dial">+{c.dialCode}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      {error && <p className="field-error">{error}</p>}
    </div>
  );
}

/**
 * Validates a national number against the selected country's expected digit
 * length. Returns an error message, or '' if valid.
 */
export function validatePhone(iso2: string, rawValue: string): string {
  const digits = rawValue.trim();
  if (!digits) return 'Phone number is required.';
  if (!/^\d+$/.test(digits)) return 'Phone number must contain digits only.';

  const c = findCountry(iso2);
  if (digits.length !== c.digits) {
    return `${c.name} numbers must be ${c.digits} digits.`;
  }
  return '';
}

/** Builds the full E.164-ish string to send to the backend, e.g. "+91XXXXXXXXXX" */
export function toFullPhoneNumber(iso2: string, rawValue: string): string {
  const c = findCountry(iso2);
  return `+${c.dialCode}${rawValue.trim()}`;
}